import { useMemo } from 'react';
import { Alert, Box, Button, Card, Container, Stack } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import DataGridCustom from 'src/components/data-grid-custom';
import HeaderBreadcrumbs from 'src/components/header-bread-crumbs/HeaderBreadcrumbs';
import Iconify from 'src/components/iconify';
import Page from 'src/components/page/page';
import SegmentedTabs from 'src/components/segmented-tabs/segmented-tabs';
import { BenefitsTableToolbar } from 'src/modules/benefits/benefits-list/components/benefits-table-toolbar';
import {
  BenefitDateCell,
  BenefitImageCell,
  BenefitTypeCell,
} from 'src/modules/benefits/components/benefit-table-cells';
import {
  BenefitStatusCell,
  BenefitStatusHeader,
} from 'src/modules/benefits/components/benefit-status-cell';
import AchievementStatusBadge from 'src/modules/trails/components/achievement-status-badge';
import { paths } from 'src/routes/paths';
import { LPBenefitListItem } from 'src/types/lp-benefits';
import { formatBenefitDate as formatDate } from 'src/utils/benefit-dates';
import { getLpCouponsColumns } from '../coupons-lp-list/components/lp-coupons-table-columns/lp-coupons-table-columns';
import useLPBenefitsList from './benefits-lp-list.hook';

const tabs = [
  { value: 'pending' as const, label: 'Pendentes', icon: 'solar:clock-circle-linear' },
  { value: 'approved' as const, label: 'Aprovados', icon: 'solar:check-circle-linear' },
  { value: 'declined' as const, label: 'Recusados', icon: 'solar:close-circle-linear' },
];

export default function LPBenefitsList() {
  const hook = useLPBenefitsList();
  const navigate = useNavigate();
  const columns = useMemo<GridColDef[]>(() => {
    const legacy = getLpCouponsColumns(hook.roles).map((column) => ({
      ...column,
      minWidth: column.field === 'name' ? 210 : 135,
      ...(['startDate', 'endDate', 'createdAt'].includes(column.field)
        ? {
            minWidth: 170,
            valueFormatter: ({ value }: { value: string }) => formatDate(value),
            renderCell: ({ value }: GridRenderCellParams) => (
              <BenefitDateCell value={value} formatDate={formatDate} />
            ),
            ...(['startDate', 'endDate'].includes(column.field)
              ? {
                  headerName: column.field === 'startDate' ? 'Data de início' : 'Data de fim',
                }
              : {}),
          }
        : {}),
    }));
    return [
      {
        field: 'image',
        headerName: 'Imagem',
        width: 112,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ value, row }: GridRenderCellParams<LPBenefitListItem>) => (
          <BenefitImageCell value={value} name={row.name} />
        ),
      },
      legacy[0],
      {
        field: 'type',
        headerName: 'Tipo',
        minWidth: 180,
        flex: 0.9,
        align: 'left',
        headerAlign: 'left',
        disableColumnMenu: true,
        renderCell: ({ value }: GridRenderCellParams) => <BenefitTypeCell value={value} />,
      },
      ...legacy.slice(1),
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 160,
        flex: 0.7,
        align: 'left',
        headerAlign: 'left',
        disableColumnMenu: true,
        renderHeader:
          hook.approvalStatus === 'approved' ? () => <BenefitStatusHeader /> : undefined,
        renderCell: ({ value, row }: GridRenderCellParams<LPBenefitListItem>) =>
          hook.approvalStatus === 'approved' && row.lpEventsData?.approvalStatus === 'approved' ? (
            <BenefitStatusCell
              value={value}
              disabled={
                hook.isLoading ||
                hook.updatingBenefitIds.includes(`${row.benefitType}:${row.benefitId}`)
              }
              onChange={
                hook.canChangeBenefitStatus
                  ? (status) => hook.onChangeBenefitStatus(row, status)
                  : undefined
              }
            />
          ) : (
            <Box sx={{ width: 1, display: 'flex', justifyContent: 'flex-start' }}>
              <AchievementStatusBadge status={String(value || '')} variant="soft" />
            </Box>
          ),
      },
    ];
  }, [
    hook.roles,
    hook.approvalStatus,
    hook.canChangeBenefitStatus,
    hook.isLoading,
    hook.updatingBenefitIds,
    hook.onChangeBenefitStatus,
  ]);
  const canReview = hook.roles?.some((role) => ['cn_admin', 'lp_promoter'].includes(role));

  return (
    <Page title="Cupons & Produtos — Viva Visitante">
      <Container maxWidth="xl">
        <HeaderBreadcrumbs heading="Cupons & Produtos" links={[]} />
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <SegmentedTabs
            ariaLabel="Homologação dos benefícios do Viva Visitante"
            currentTab={hook.approvalStatus}
            tabs={tabs}
            onChangeTab={hook.onChangeStatus}
          />
          {hook.canCreate && (
            <Button
              variant="contained"
              startIcon={<Iconify icon="mingcute:add-line" />}
              onClick={() => navigate(paths.dashboard.lpBenefitCreate)}
            >
              Cadastrar Benefício
            </Button>
          )}
        </Stack>
        {hook.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {hook.error}
          </Alert>
        )}
        <Card sx={{ p: 2 }}>
          <DataGridCustom
            animatedEachRow
            excludeExportButton
            checkboxSelection={false}
            columns={columns}
            rows={(hook.benefits?.data || []).map((item) => ({
              ...item,
              id: `${item.benefitType}:${item.benefitId}`,
            }))}
            customFilters={
              <BenefitsTableToolbar
                searchText={hook.search}
                searchTooltip="A pesquisa considera apenas o nome do cupom ou produto."
                setSearchText={hook.onSearch}
                selectedFiltersCount={0}
              />
            }
            totalRowsCount={hook.benefits?.total || 0}
            filteredRowsCount={hook.benefits?.totalFiltered || 0}
            initialVisibleColumns={{
              eventName: Boolean(canReview),
              creatorName: Boolean(canReview),
              availableQuantity: !canReview,
            }}
            sortModel={hook.sortModel}
            setSortModel={hook.onSort}
            paginationModel={hook.paginationModel}
            setPaginationModel={hook.setPaginationModel}
            isLoading={hook.isLoading}
            onRowClick={({ row }) =>
              navigate(paths.dashboard.lpBenefitDetails(row.benefitId, row.benefitType))
            }
            height={550}
          />
        </Card>
      </Container>
    </Page>
  );
}
